/** @jsxImportSource @emotion/react */
import { Buttons, Dialogs } from '@zip/business-components';
import { useAuth0, useMerchantData } from 'contexts';
import { FeatureFlag, PageAction, PageRoute } from 'enums';
import { MfaEnrollmentStatus } from 'enums/MfaEnrollmentStatus.enum';
import { Constants } from 'global';
import { useFeatureFlagEnabled } from 'hooks';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logEvent } from 'utils';
import { useMfaFunctions } from 'utils/useMfaFunctions';
import * as styles from './EnableMFADialog.styles';
import { RegularUserSkipLimit } from './EnableMfaDialogConstants';
import {
  DialogContent,
  PopupUserMode,
  RegularUserMode,
} from './EnableMfaDialogTypes';
import {
  confirmLocalMfaPopupForToday,
  hadLocalMfaPopupForToday,
  isAdminUserMode,
  makeContentInDialog,
} from './EnableMfaDialogUtils';

const EnableMFADialogModule = (): JSX.Element => {
  const { logout } = useAuth0();
  const navigate = useNavigate();
  const { shouldShowMfaManagment: isAdminUser } = useMerchantData();
  const { getMfaEnrollmentAsync, skipMfaAsync, acceptMfaAsync } =
    useMfaFunctions();

  const { currentUserId } = useMerchantData();
  const [shouldOpen, setShouldOpen] = useState<boolean>(false);
  const [content, setContent] = useState<DialogContent | undefined>(undefined);

  const [mfaSkipCount, setMfaSkipCount] = useState(0);
  const isMountedRef = useRef(true);

  const makeDialogContent = (userMode: PopupUserMode): DialogContent => {
    const isAdminMode = isAdminUserMode(userMode);

    const proceedFunc: () => Promise<void> = isAdminMode
      ? (): Promise<void> => {
          navigate(`${PageRoute.Settings}${PageRoute.Users}`);
          logEvent(PageAction.mfaModalAdminEnableSettings);
          return Promise.resolve();
        }
      : async (): Promise<void> => {
          await acceptMfaAsync();
          logEvent(PageAction.mfaModalSelfSetup, {
            skipCount: mfaSkipCount,
          });
          if (isMountedRef.current) {
            setShouldOpen(false);
          }
          logout();
        };

    const cancelFunc = isAdminMode
      ? (): Promise<void> => {
          if (isMountedRef.current) {
            setShouldOpen(false);
          }
          logEvent(PageAction.mfaModalAdminRemindLater);
          return Promise.resolve();
        }
      : async (): Promise<void> => {
          await skipMfaAsync();
          logEvent(PageAction.mfaModalSelfSkip, {
            skipCount: mfaSkipCount,
          });
          if (isMountedRef.current) {
            setShouldOpen(false);
          }
        };

    const content = makeContentInDialog(userMode, proceedFunc, cancelFunc);

    return content;
  };

  const selectiveMfaEnabled = useFeatureFlagEnabled(
    FeatureFlag.EnableSelectiveMFA
  );

  useEffect(() => {
    const abortController = new AbortController();

    const initialiseDialogContent = async (): Promise<void> => {
      if (hadLocalMfaPopupForToday(currentUserId)) {
        return;
      }

      const today = new Date();
      const endOf2024 = Constants.expiryDate.mfaPopupStop;

      const isAdmin = isAdminUser();
      const userMfaEnrollment = await getMfaEnrollmentAsync(
        abortController.signal
      );

      // Check if component is still mounted before proceeding
      if (!isMountedRef.current) {
        return;
      }

      const enrollmentStatus =
        userMfaEnrollment && MfaEnrollmentStatus[userMfaEnrollment?.status];

      const isInvited =
        userMfaEnrollment && enrollmentStatus === MfaEnrollmentStatus.Invited;

      const isDisabled =
        !userMfaEnrollment || enrollmentStatus === MfaEnrollmentStatus.Disabled;

      if (isMountedRef.current) {
        setMfaSkipCount(userMfaEnrollment?.skippedCount ?? 0);
      }

      const regularUserMode = (): RegularUserMode => ({
        skipCountUsed: userMfaEnrollment.skippedCount,
      });

      const [mode, shouldShow]: [PopupUserMode, boolean] = isInvited
        ? [
            regularUserMode(),
            isInvited && userMfaEnrollment.skippedCount < RegularUserSkipLimit,
          ]
        : ['adminUserMode', isAdmin && isDisabled && today <= endOf2024];

      const content = makeDialogContent(mode);

      if (shouldShow && isMountedRef.current) {
        setContent(content);
        setShouldOpen(shouldShow);
        confirmLocalMfaPopupForToday(currentUserId);
      }
    };

    initialiseDialogContent().catch((err) => {
      // Only log error if the request wasn't aborted
      if (err.name !== 'AbortError') {
        console.error(err);
      }
    });

    return (): void => {
      isMountedRef.current = false;
      abortController.abort();
    };
  }, []);

  return (
    selectiveMfaEnabled && (
      <Dialogs.Basic
        open={shouldOpen}
        mobileFullscreen
        showClose={false}
        css={styles.mfaDialog}
      >
        <div css={styles.mfaDialogContent}>
          <div css={styles.logoWrapper(content?.backgroundColor)}>
            <img src={content?.logo} alt="MFA Logo" width={160} height={160} />
          </div>

          <div css={styles.mfaDialogContentWithPadding}>
            <p css={styles.title}>{content?.title}</p>
            <p css={styles.subtitle}>{content?.subtitle}</p>

            <Buttons.Primary onClick={content?.okButtonProps.task} fullWidth>
              {content?.okButtonProps.label}
            </Buttons.Primary>
            <p css={styles.hint}>{content?.additionalHint}</p>

            <Buttons.Text
              css={styles.textButton}
              onClick={content?.cancelButtonProps.task}
            >
              {content?.cancelButtonProps.label}
            </Buttons.Text>
          </div>
        </div>
      </Dialogs.Basic>
    )
  );
};

export default EnableMFADialogModule;
